import type { ComponentPropsWithoutRef } from "react";
import {
  RiAlertFill,
  RiDiscountPercentFill,
  RiInformationFill,
  RiLightbulbFlashLine,
} from "react-icons/ri";
import type { Product } from "~types/api/product.types";

import { Text } from "~/components/ui/typography";
import { getProductPackagingInfo, getProductPrice } from "~/services/product";
import { cn } from "~/utils/classnames";
import { formatCurrency, formatPercentage } from "~/utils/formatters";

interface ProductPackagingInfoProps extends ComponentPropsWithoutRef<"div"> {
  product: Product;
  quantity: number;
}

export const ProductPackagingInfo = ({
  product,
  quantity,
  className,
  ...props
}: ProductPackagingInfoProps) => {
  const productPrice = Number(getProductPrice(product) ?? 0);
  const currentStock = Number(product.stocks?.[0]?.currentStock ?? 0);

  const packagingInfo = getProductPackagingInfo(product);
  return (
    <div className={cn("text-sm font-medium", className)} {...props}>
      {packagingInfo?.isProductInEndOfLifeWithDiscount ? ( // Case E
        quantity < currentStock ? (
          <p className="flex items-center gap-2 text-warning-600">
            <RiDiscountPercentFill className="mt-px size-5 shrink-0" />
            Ajoutez {currentStock - quantity} produit{currentStock - quantity > 1 ? "s" : ""} pour
            économiser{" "}
            {formatCurrency(currentStock * productPrice * packagingInfo.packagingDiscountRate)}.
          </p>
        ) : (
          <p className="flex items-center gap-2 text-success-600">
            <RiDiscountPercentFill className="mt-px size-5 shrink-0" />
            Excellent, vous bénéficiez de{" "}
            {formatCurrency(currentStock * productPrice * packagingInfo.packagingDiscountRate)} de
            remise.
          </p>
        )
      ) : packagingInfo?.isOrderableNormallyWithDiscountByPackaging && quantity > 1 ? ( // Case B
        quantity % packagingInfo.packagingQuantity === 0 ? (
          <p className="flex items-center gap-2 text-success-600">
            <RiDiscountPercentFill className="mt-px size-5 shrink-0" />
            Excellent, vous bénéficiez de{" "}
            {formatCurrency(quantity * productPrice * packagingInfo.packagingDiscountRate)} de
            remise.
          </p>
        ) : (
          <p className="flex items-center gap-2 text-warning-600">
            <RiDiscountPercentFill className="mt-px size-5 shrink-0" />
            Ajoutez {packagingInfo.packagingQuantity -
              (quantity % packagingInfo.packagingQuantity)}{" "}
            produit
            {packagingInfo.packagingQuantity - (quantity % packagingInfo.packagingQuantity) > 1
              ? "s"
              : ""}{" "}
            pour économiser{" "}
            {formatCurrency(
              (quantity +
                (packagingInfo.packagingQuantity - (quantity % packagingInfo.packagingQuantity))) *
                productPrice *
                packagingInfo.packagingDiscountRate
            )}
            .
          </p>
        )
      ) : packagingInfo?.isOrderableOnlyByPackaging ? ( // Case C
        quantity % packagingInfo.packagingQuantity === 0 ? (
          <p className="flex items-center gap-2 text-success-600">
            <RiInformationFill className="mt-px size-5 shrink-0" />
            Vente par {packagingInfo.packagingUnit} ({packagingInfo.packagingQuantity} pièces)
            uniquement.
          </p>
        ) : (
          <p className="flex items-center gap-2 text-warning-600">
            <RiAlertFill className="mt-px size-5 shrink-0" />
            Vente par {packagingInfo.packagingUnit} ({packagingInfo.packagingQuantity} pièces)
            uniquement.
            <br />
            Veuillez ajouter{" "}
            {packagingInfo.packagingQuantity - (quantity % packagingInfo.packagingQuantity)} produit
            {packagingInfo.packagingQuantity - (quantity % packagingInfo.packagingQuantity) > 1
              ? "s"
              : ""}
            .
          </p>
        )
      ) : packagingInfo?.isOrderableOnlyByPackagingWithDiscount ? ( // Case D
        quantity % packagingInfo.packagingQuantity === 0 ? (
          <p className="flex items-center gap-2 text-success-600">
            <RiDiscountPercentFill className="mt-px size-5 shrink-0" />
            Vente par {packagingInfo.packagingUnit} ({packagingInfo.packagingQuantity} pièces)
            uniquement.
            <br />
            Excellent, vous bénéficiez de{" "}
            {formatCurrency(quantity * productPrice * packagingInfo.packagingDiscountRate)} de
            remise.
          </p>
        ) : (
          <p className="flex items-center gap-2 text-warning-600">
            <RiDiscountPercentFill className="mt-px size-5 shrink-0" />
            Vente par {packagingInfo.packagingUnit} ({packagingInfo.packagingQuantity} pièces)
            uniquement.
            <br />
            Veuillez ajouter{" "}
            {packagingInfo.packagingQuantity - (quantity % packagingInfo.packagingQuantity)} produit
            {packagingInfo.packagingQuantity - (quantity % packagingInfo.packagingQuantity) > 1
              ? "s"
              : ""}{" "}
            et économisez{" "}
            {formatCurrency(
              (quantity +
                (packagingInfo.packagingQuantity - (quantity % packagingInfo.packagingQuantity))) *
                productPrice *
                packagingInfo.packagingDiscountRate
            )}
            .
          </p>
        )
      ) : null}
    </div>
  );
};

interface ProductPackagingBadgeProps extends ComponentPropsWithoutRef<"div"> {
  product: Product;
  quantity: number;
}

export const ProductPackagingBadge = ({
  product,
  className,
  ...props
}: ProductPackagingBadgeProps) => {
  const packagingInfo = getProductPackagingInfo(product);

  if (
    packagingInfo?.isProductInEndOfLifeWithDiscount ||
    (!packagingInfo?.isOrderableNormallyWithDiscountByPackaging &&
      !packagingInfo?.isOrderableOnlyByPackaging &&
      !packagingInfo?.isOrderableOnlyByPackagingWithDiscount)
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex max-w-max items-center gap-3 rounded-full border border-primary-900/5 bg-primary-500 px-3 py-0.5 text-grey-900",
        className
      )}
      {...props}
    >
      <RiLightbulbFlashLine className="size-5 text-grey-900" />
      <Text size="sm">
        {packagingInfo?.isProductInEndOfLifeWithDiscount
          ? null
          : packagingInfo?.isOrderableNormallyWithDiscountByPackaging
            ? `Vente par ${packagingInfo.packagingUnit} (${packagingInfo.packagingQuantity} pièces): remise de ${formatPercentage(packagingInfo.packagingDiscountRate)}`
            : packagingInfo?.isOrderableOnlyByPackaging
              ? `Vente par ${packagingInfo.packagingUnit} (${packagingInfo.packagingQuantity} pièces) uniquement`
              : packagingInfo?.isOrderableOnlyByPackagingWithDiscount
                ? `Vente par ${packagingInfo.packagingUnit} (${packagingInfo.packagingQuantity} pièces) uniquement`
                : null}
      </Text>
    </div>
  );
};
